import { CSSProperties, PropsWithChildren, useState } from 'react';
import classNames from 'classnames';
import type { StaticImageData } from 'next/image';
import { BREAKPOINT_SM_SIZE } from '@alltrails/denali/tokens';
import { useIntl } from 'react-intl';
import useInterval from '@alltrails/core/hooks/useInterval';
import IconButton from '@alltrails/denali/components/IconButton';
import Play from '@alltrails/denali/icons/Play';
import Pause from '@alltrails/denali/icons/Pause';
import styles from './styles.module.scss';

const MEDIA_QUERY_SMALL_DOWN = `(max-width: ${BREAKPOINT_SM_SIZE}px)`;

type Props = {
  buttonContainerStyle?: CSSProperties;
  className: string;
  images: { alt: string; w2880Jpg: StaticImageData; w2880Avif: StaticImageData; w750Jpg: StaticImageData; w750Avif: StaticImageData }[];
};

const DURATION = 6000;

const RotatingHero = ({ images, className, buttonContainerStyle, children }: PropsWithChildren<Props>) => {
  const intl = useIntl();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [delay, setDelay] = useState<number | null>(DURATION);

  const handleSlideClick = (index: number) => {
    // pause or restart image rotation
    setCurrentImageIndex(index);
    setPaused(!paused);
    setDelay(paused ? DURATION : null);
  };

  const handlePlayPauseClick = () => {
    setPaused(!paused);
    setDelay(paused ? DURATION : null);
  };

  useInterval(() => {
    let nextIndex = currentImageIndex + 1;

    if (nextIndex === images.length) {
      nextIndex = 0;
    }

    setCurrentImageIndex(nextIndex);
    setDelay(DURATION);
  }, delay);

  return (
    <section aria-label={intl.formatMessage({ defaultMessage: 'Hero carousel' })}>
      <div className={`${className} ${styles.container}`}>
        {images.map((image, index) => (
          <picture className={classNames(styles.picture, index !== currentImageIndex && styles.none)} key={image.w2880Jpg.src}>
            <source media={MEDIA_QUERY_SMALL_DOWN} srcSet={image.w750Avif.src} type="image/avif" />
            <source media={MEDIA_QUERY_SMALL_DOWN} srcSet={image.w750Jpg.src} type="image/jpg" />
            <source srcSet={image.w2880Avif.src} type="image/avif" />
            <img
              alt={image.alt}
              src={image.w2880Jpg.src}
              className={styles.heroImage}
              fetchPriority={index === 0 ? "high" : "low"}
            />
          </picture>
        ))}
        {children}
        <div className={styles.slideButtons} style={buttonContainerStyle}>
          <IconButton
            className={styles.playPauseButton}
            icon={{ Component: paused ? Play : Pause }}
            onClick={handlePlayPauseClick}
            size="sm"
            testId={`rotating-hero-${paused ? 'play' : 'pause'}`}
            title={paused ? intl.formatMessage({ defaultMessage: 'Play slideshow' }) : intl.formatMessage({ defaultMessage: 'Pause slideshow' })}
            ariaLabel={paused ? intl.formatMessage({ defaultMessage: 'Play slideshow' }) : intl.formatMessage({ defaultMessage: 'Pause slideshow' })}
            variant="flat"
          />
          {images.map((image, index) => {
            const progressSpanStyle: CSSProperties = {};
            const innerSpanClassNames: (string | undefined)[] = [];

            if (index < currentImageIndex) {
              innerSpanClassNames.push(styles.slideButtonSpanComplete);
            } else if (index === currentImageIndex) {
              innerSpanClassNames.push(styles.slideButtonSpanActive);
              progressSpanStyle.animationDuration = `${DURATION}ms`;
              progressSpanStyle.animationPlayState = `${paused ? 'paused' : 'running'}`;
            }

            return (
              <button
                aria-label={intl.formatMessage({ defaultMessage: 'Carousel image {index}' }, { index: index + 1 })}
                key={image.alt}
                type="button"
                className={styles.slideButton}
                onClick={() => handleSlideClick(index)}
                aria-current={index === currentImageIndex}
              >
                <span className={styles.slideButtonSpan}>
                  <span className={classNames(innerSpanClassNames)} style={progressSpanStyle} />
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default RotatingHero;
